import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, Edit, Trash2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { userService } from '../../_services';

function AddOverride({
    text,
    whName,
    whId,
    whPrefix,
    whProfilePagin,
    whSearch,
    whEmail,
    closeAddOverridePopup,
    openEditProfilePopup,
    openmanageGroupSection
}) {
    const [loading, setLoading] = useState(true);
    const [btnLoader, setBtnLoader] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [chainNamesList, setChainNamesList] = useState([]);
    const [overrides, setOverrides] = useState([]);
    const [noInitialOverrides, setNoInitialOverrides] = useState(true);
    const [isChecked, setIsChecked] = useState(false);
    const [isPreGroupSpecific, setIsPreGroupSpecific] = useState(false);
    const [predefinedGroups, setPredefinedGroups] = useState(null);
    const [openPopover, setOpenPopover] = useState({});

    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    }, []);

    const fetchPharmacyChainList = useCallback(async () => {
        try {
            const response = await userService.fetchPharmacyChainList('a', 'a');
            console.log('🔍 AddOverride: fetchPharmacyChainList response:', response);
            
            // Handle response as text then parse JSON (like the old class component did)
            const text = await response.text();
            const apidata = text ? JSON.parse(text) : [];
            
            console.log('🔍 AddOverride: fetchPharmacyChainList parsed apidata:', apidata);
            if (apidata?.length > 0) {
                setChainNamesList(apidata);
                console.log('✅ AddOverride: chainNamesList set with', apidata.length, 'items');
            } else {
                console.log('⚠️ AddOverride: No chain data received');
            }
        } catch (error) {
            console.error('❌ AddOverride: Error fetching chain list:', error);
        }
    }, []);

    const fetchUserPharmacyChainList = useCallback(async (whname, wholesalerId) => {
        setLoading(true);
        try {
            const response = await userService.fetchUserPharmacyChainList(whname, wholesalerId, 0);
            const text = await response.text();
            const apidata = text && JSON.parse(text);

            if (apidata?.length > 0) {
                // Check if data is group-specific
                if (apidata[0].isGroupSpecific) {
                    const overrideData = apidata[0].chainNames?.map(item => ({
                        chainName: item.chainName,
                        comm: item.comm,
                        editable: false,
                        id: Math.floor(10000 + Math.random() * 90000)
                    })) || [];
                    setOverrides(overrideData);
                    setNoInitialOverrides(overrideData.length === 0);
                    setIsChecked(true);
                    setIsPreGroupSpecific(true);
                    setPredefinedGroups(apidata[0].groupNumbers || null);
                } else {
                    const overrideData = apidata[0].chainNames?.map(item => ({
                        chainName: item.chainName,
                        comm: item.comm,
                        editable: false,
                        id: Math.floor(10000 + Math.random() * 90000)
                    })) || [];
                    setOverrides(overrideData);
                    setNoInitialOverrides(overrideData.length === 0);
                    setIsChecked(false);
                }
            } else {
                // Add initial empty row
                setOverrides([{
                    chainName: '',
                    comm: '',
                    editable: true,
                    id: Math.floor(10000 + Math.random() * 90000)
                }]);
                setNoInitialOverrides(true);
            }
        } catch (error) {

            setError('Error fetching chain list');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (whName && whId) {
            fetchPharmacyChainList();
            fetchUserPharmacyChainList(whName, whId);
        }
    }, [whName, whId, fetchPharmacyChainList, fetchUserPharmacyChainList]);

    const addOverrideRow = useCallback(() => {
        const newRow = {
            chainName: '',
            comm: '',
            editable: true,
            id: Math.floor(10000 + Math.random() * 90000)
        };
        setOverrides(prev => [...prev, newRow]);
    }, []);

    const handleOverrideChange = useCallback((index, field, value) => {
        setOverrides(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    }, []);

    const handleSelectChain = useCallback((index, chain) => {
        setOverrides(prev => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                chainName: chain.chainName
            };
            return updated;
        });
    }, []);

    const handleEdit = useCallback((index) => {
        setOverrides(prev => {
            const updated = prev.map((item, idx) => ({
                ...item,
                editable: idx === index
            }));
            return updated;
        });
    }, []);

    const saveData = useCallback((index) => {
        const override = overrides[index];
        
        if (!override.chainName || !override.comm) {
            setError("Fields can't be blank and commission should be a numeric value e.g. 0.10");
            setTimeout(() => setError(''), 3000);
            return;
        }

        if (!userService.validateCurrency(override.comm)) {
            setError("Commission should be a numeric value e.g. 0.10");
            setTimeout(() => setError(''), 3000);
            return;
        }

        if (parseFloat(override.comm) > 10) {
            setError('Commission should not be more than 10');
            setTimeout(() => setError(''), 3000);
            return;
        }

        if (parseFloat(override.comm) <= 0) {
            setError('Commission should be more than 0');
            setTimeout(() => setError(''), 3000);
            return;
        }

        setOverrides(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], editable: false };
            return updated;
        });
        setError('');
    }, [overrides]);

    const handleCancel = useCallback((index) => {
        const override = overrides[index];
        
        if (!override.chainName && !override.comm) {
            setOverrides(prev => {
                const updated = [...prev];
                updated.splice(index, 1);
                return updated;
            });
        } else {
            setOverrides(prev => {
                const updated = [...prev];
                updated[index] = { ...updated[index], editable: false };
                return updated;
            });
        }
        setError('');
    }, [overrides]);

    const deleteRow = useCallback((index) => {
        setOverrides(prev => {
            const updated = [...prev];
            updated.splice(index, 1);
            return updated;
        });
    }, []);

    const saveOverrides = useCallback(async (overrideData) => {
        console.log('🔍 AddOverride: saveOverrides called with:', overrideData);
        console.log('🔍 AddOverride: flag (noInitialOverrides):', noInitialOverrides ? 0 : 1);
        console.log('🔍 AddOverride: whName:', whName, 'whId:', whId);
        
        try {
            const flag = noInitialOverrides ? 0 : 1;
            // ✅ updatePharmacyChainList returns already-parsed data, not a Response object
            const data = await userService.updatePharmacyChainList(overrideData, whName, whId, flag);
            console.log('🔍 AddOverride: API data (already parsed):', data);

            if (data?.length > 0) {
                console.log('🔍 AddOverride: data[0]:', data[0]);
                console.log('🔍 AddOverride: status_code value:', data[0].status_code);
                console.log('🔍 AddOverride: status_code type:', typeof data[0].status_code);
                console.log('🔍 AddOverride: status_message:', data[0].status_message);
                
                // Check for success (status_code can be "1", 1, or "success")
                const isSuccess = data[0].status_code === "1" || 
                                 data[0].status_code === 1 || 
                                 data[0].status_code === "success" ||
                                 data[0].status_message?.toLowerCase() === "success";
                
                console.log('🔍 AddOverride: isSuccess:', isSuccess);
                
                if (isSuccess) {
                    setSuccessMsg(data[0].status_message);
                    console.log('✅ AddOverride: Chain list saved successfully');
                    fetchUserPharmacyChainList(whName, whId);
                    setTimeout(() => setSuccessMsg(''), 3000);
                } else {
                    setError(data[0].status_message || 'Error saving chain list');
                    console.error('❌ AddOverride: Error from API. status_code:', data[0].status_code);
                    setTimeout(() => setError(''), 3000);
                }
            } else {
                setError('Oops! Something went wrong. Try Again.');
                console.error('❌ AddOverride: No data returned from API');
                setTimeout(() => setError(''), 3000);
            }
        } catch (error) {
            console.error('❌ AddOverride: Error saving chain list:', error);
            setError('Oops! Something went wrong. Try Again.');
            setTimeout(() => setError(''), 3000);
        } finally {
            setBtnLoader(false);
            setLoading(false);
        }
    }, [noInitialOverrides, whName, whId, fetchUserPharmacyChainList]);

    const handleSaveSubmit = useCallback((e) => {
        e.preventDefault();
        console.log('🔍 AddOverride: handleSaveSubmit called');
        setBtnLoader(true);
        setLoading(true);
        setError('');

        const isEditMode = overrides.some(row => row.editable === true);
        console.log('🔍 AddOverride: isEditMode:', isEditMode);
        if (isEditMode) {
            setError('Please save all the fields first');
            setBtnLoader(false);
            setLoading(false);
            return;
        }

        const overrideData = overrides.map(item => ({
            ChainName: item.chainName,
            Comm: item.comm
        }));
        console.log('🔍 AddOverride: overrideData to save:', overrideData);

        if (overrideData.length === 0) {
            setError('Please add pharmacy chain list');
            setBtnLoader(false);
            setLoading(false);
            return;
        }

        saveOverrides(overrideData);
    }, [overrides, saveOverrides]);

    const handleBack = useCallback(() => {
        closeAddOverridePopup(whName, whId, whPrefix, whProfilePagin, whSearch, whEmail);
        openEditProfilePopup(whName, whId, whPrefix, whProfilePagin, whSearch);
    }, [closeAddOverridePopup, openEditProfilePopup, whName, whId, whPrefix, whProfilePagin, whSearch, whEmail]);

    const handleManageGroupSection = useCallback(() => {
        closeAddOverridePopup(whName, whId, whPrefix, whProfilePagin, whSearch, whEmail);
        openmanageGroupSection(whName, whId, whPrefix, whProfilePagin, whSearch);
    }, [closeAddOverridePopup, openmanageGroupSection, whName, whId, whPrefix, whProfilePagin, whSearch, whEmail]);

    const handleCheckboxChange = useCallback(() => {
        setIsChecked(prev => !prev);
    }, []);

    const canToggleCheckbox = isChecked || (!isChecked && overrides.length === 0);

    return (
        <Dialog open={true} onOpenChange={handleBack}>
            <DialogContent className="max-w-5xl">
                <DialogHeader>
                    <DialogTitle>{text || 'Chain List'}</DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="flex justify-center items-center py-4">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : <>
                <form id="add-override-form" onSubmit={handleSaveSubmit} className="space-y-6">
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <div className="space-y-2">
                            <h2 className="text-lg font-semibold">Chain List</h2>
                            
                            {/* Make Group Specific Checkbox */}
                            <div className="flex items-center space-x-2">
                                <Checkbox
                                    id="group-specific"
                                    checked={isChecked}
                                    onCheckedChange={handleCheckboxChange}
                                    disabled={!canToggleCheckbox}
                                />
                                <label
                                    htmlFor="group-specific"
                                    className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 ${!canToggleCheckbox ? 'opacity-50' : 'cursor-pointer'}`}
                                >
                                    Make Group Specific
                                </label>
                            </div>

                            {/* Display predefined groups if they exist */}
                            {predefinedGroups && predefinedGroups.length > 0 && (
                                <div className="mt-2">
                                    <label className="text-sm font-medium text-muted-foreground">Group Numbers:</label>
                                    <div className="flex flex-wrap gap-2 mt-1">
                                        {predefinedGroups.map((item, index) => (
                                            <div key={index} className="px-2 py-1 bg-secondary text-secondary-foreground rounded text-sm">
                                                {item.groupNumber}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <div className="flex gap-2">
                            {isChecked && (
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={handleManageGroupSection}
                                    disabled={btnLoader}
                                >
                                    {isPreGroupSpecific ? 'Edit' : 'Add'} Group Section
                                </Button>
                            )}
                            {!isChecked && (
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={addOverrideRow}
                                    disabled={btnLoader}
                                >
                                    Add New Chain
                                </Button>
                            )}
                        </div>
                    </div>
                </div>

                {overrides.length > 0 ? (
                    <div className="border rounded-lg overflow-hidden">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Commission</TableHead>
                                    {!isChecked && <TableHead className="w-[150px]">Actions</TableHead>}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {overrides.map((override, index) => (
                                    <TableRow key={override.id}>
                                        <TableCell>
                                            {override.editable ? (
                                                <Popover 
                                                    open={openPopover[index] || false} 
                                                    onOpenChange={(open) => setOpenPopover(prev => ({ ...prev, [index]: open }))}
                                                >
                                                    <PopoverTrigger asChild>
                                                        <Input
                                                            type="text"
                                                            value={override.chainName}
                                                            onChange={(e) => handleOverrideChange(index, 'chainName', e.target.value)}
                                                            onFocus={() => setOpenPopover(prev => ({ ...prev, [index]: true }))}
                                                            placeholder="Search chain..."
                                                            className="w-full"
                                                        />
                                                    </PopoverTrigger>
                                                    <PopoverContent 
                                                        className="w-[300px] p-0" 
                                                        align="start"
                                                        onOpenAutoFocus={(e) => e.preventDefault()}
                                                    >
                                                        <div className="max-h-[200px] overflow-y-auto">
                                                            {(() => {
                                                                console.log('🔍 AddOverride Popover: chainNamesList:', chainNamesList);
                                                                console.log('🔍 AddOverride Popover: override.chainName:', override.chainName);
                                                                const filtered = chainNamesList.filter(chain => 
                                                                    !override.chainName || 
                                                                    chain.chainName.toLowerCase().includes(override.chainName.toLowerCase())
                                                                );
                                                                console.log('🔍 AddOverride Popover: filtered chains:', filtered);
                                                                return null;
                                                            })()}
                                                            {chainNamesList
                                                                .filter(chain => 
                                                                    !override.chainName || 
                                                                    chain.chainName.toLowerCase().includes(override.chainName.toLowerCase())
                                                                )
                                                                .length > 0 ? (
                                                                chainNamesList
                                                                    .filter(chain => 
                                                                        !override.chainName || 
                                                                        chain.chainName.toLowerCase().includes(override.chainName.toLowerCase())
                                                                    )
                                                                    .map((chain, idx) => (
                                                                        <div
                                                                            key={idx}
                                                                            className="px-3 py-2 cursor-pointer hover:bg-accent text-sm border-b last:border-b-0"
                                                                            onMouseDown={(e) => {
                                                                                e.preventDefault();
                                                                                handleSelectChain(index, chain);
                                                                                setOpenPopover(prev => ({ ...prev, [index]: false }));
                                                                            }}
                                                                        >
                                                                            {chain.chainName}
                                                                        </div>
                                                                    ))
                                                            ) : (
                                                                <div className="px-3 py-2 text-sm text-muted-foreground">
                                                                    No chain found.
                                                                </div>
                                                            )}
                                                        </div>
                                                    </PopoverContent>
                                                </Popover>
                                            ) : (
                                                override.chainName
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {override.editable ? (
                                                <Input
                                                    type="text"
                                                    value={override.comm}
                                                    onChange={(e) => handleOverrideChange(index, 'comm', e.target.value)}
                                                />
                                            ) : (
                                                override.comm
                                            )}
                                        </TableCell>
                                        {!isChecked && (
                                            <TableCell>
                                                {override.editable ? (
                                                    <div className="flex gap-2">
                                                        <Button
                                                            type="button"
                                                            size="sm"
                                                            onClick={() => saveData(index)}
                                                        >
                                                            Save
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            size="sm"
                                                            variant="secondary"
                                                            onClick={() => handleCancel(index)}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <div className="flex gap-2">
                                                        <Button
                                                            type="button"
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => handleEdit(index)}
                                                        >
                                                            <Edit className="h-4 w-4" />
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => deleteRow(index)}
                                                        >
                                                            <Trash2 className="h-4 w-4 text-destructive" />
                                                        </Button>
                                                    </div>
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                ) : (
                    <div className="text-center py-8 text-muted-foreground">
                        No chain list added yet. Click "Add New Chain" to get started.
                    </div>
                )}

                {error && (
                    <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                        {error}
                    </div>
                )}

                {successMsg && (
                    <div className="text-sm text-green-600 bg-green-50 p-3 rounded-md">
                        {successMsg}
                    </div>
                )}

            </form>
                <DialogFooter>
                    <Button type="submit" form="add-override-form" disabled={loading || btnLoader}>
                        {btnLoader && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Save & Submit
                    </Button>
                    <Button type="button" variant="secondary" onClick={handleBack} disabled={btnLoader}>
                        Cancel
                    </Button>
                </DialogFooter>
            </>}

            </DialogContent>
        </Dialog>
    );
}

export default AddOverride;
